<template>
  <div>
    <Head :title="`Detail Kampus ${universitas}`" />

    <!-- HEADER -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold">Detail Kampus {{ universitas }}</h1>
    </div>

    <!-- FILTER -->
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="w-full p-4 font-semibold bg-gray-200">Filter</div>

      <div class="flex flex-wrap w-full p-6">
        <div class="w-full font-semibold lg:w-3/12">Periode Beasiswa:</div>
        <v-select
          v-model="form.periode"
          :options="periodes"
          class="w-full lg:w-9/12"
          :reduce="p => p.id"
          label="nama"
          placeholder="Pilih Periode"
        />
      </div>
    </div>

    <!-- PANEL TOTAL -->
    <div class="w-full p-4 mb-6 bg-white border rounded shadow">
      <h2 class="mb-2 text-lg font-semibold">Total Mahasiswa</h2>

      <p class="mb-2 text-sm">
        Total Mahasiswa pada kampus ini:
        <strong>{{ total }}</strong>
      </p>

      <div class="flex flex-wrap gap-3 text-sm">
        <span class="px-3 py-1 font-semibold text-white rounded" style="background:#3b82f6">
          Laki-laki: {{ laki }}
        </span>
        <span class="px-3 py-1 font-semibold text-white rounded" style="background:#db2777">
          Perempuan: {{ perempuan }}
        </span>
      </div>
    </div>

    <!-- EXPORT BUTTON -->
    <div class="flex justify-end mb-4">
      <Link
        :href="route('report.universitas.detail.export', {
          universitas: universitas,
          periode: form.periode
        })"
        class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700"
      >
        Export Excel
      </Link>
    </div>

    <!-- TABEL MAHASISWA -->
    <div class="w-full mb-10 bg-white border rounded shadow">
      <div class="px-4 py-3 font-semibold bg-gray-100 border-b">
        Daftar Mahasiswa
      </div>

      <div class="overflow-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b bg-gray-50">
              <!-- SORTING NAMA -->
              <th class="p-2 border cursor-pointer" @click="toggleSort('nama')">
                Nama
                <span v-if="sort.field === 'nama'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <!-- SORTING NIM -->
              <th class="p-2 border cursor-pointer" @click="toggleSort('nim')">
                NIM
                <span v-if="sort.field === 'nim'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <th class="p-2 border">JK</th>

              <!-- SORTING SEMESTER -->
              <th class="p-2 text-center border cursor-pointer" @click="toggleSort('semester')">
                Semester
                <span v-if="sort.field === 'semester'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <!-- SORTING PENGHASILAN -->
              <th class="p-2 text-center border cursor-pointer" @click="toggleSort('penghasilan')">
                Penghasilan Ortu
                <span v-if="sort.field === 'penghasilan'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <th class="p-2 border">Alamat</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(m, i) in sortedMahasiswa"
              :key="i"
              class="border-b hover:bg-gray-50"
            >
              <td class="p-2 border">{{ m.nama }}</td>
              <td class="p-2 border">{{ m.nim }}</td>
              <td class="p-2 border">{{ m.jk }}</td>
              <td class="p-2 text-center border">{{ m.semester }}</td>
              <td class="p-2 text-center border">{{ formatRupiah(m.penghasilan) }}</td>
              <td class="p-2 border">{{ m.alamat }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- CHART -->
    <div class="p-4 bg-white rounded shadow">
      <h2 class="mb-4 text-lg font-bold">Grafik Jumlah Mahasiswa</h2>
      <apexchart
        width="100%"
        height="420"
        type="pie"
        :options="chartOptions"
        :series="[laki, perempuan]"
      />
    </div>
  </div>
</template>



<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import VueApexCharts from 'vue3-apexcharts'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  components: { Head, Link, apexchart: VueApexCharts },
  layout: Layout,

  props: {
    universitas: String,
    mahasiswa: Array,
    total: Number,
    laki: Number,
    perempuan: Number,
    filters: Object,
    periodes: Array,
  },

  data() {
    return {
      form: {
        periode: parseInt(this.filters.periode) || null,
      },

      // SORTING STATE
      sort: {
        field: 'nama',
        direction: 'asc',
      },
    }
  },

  computed: {
    // SORTING LOGIC
    sortedMahasiswa() {
      return [...this.mahasiswa].sort((a, b) => {
        const field = this.sort.field
        const dir = this.sort.direction === 'asc' ? 1 : -1

        let x = a[field]
        let y = b[field]

        // angka vs string
        if (field === 'semester' || field === 'penghasilan') {
          x = Number(x)
          y = Number(y)
        }

        if (x < y) return -1 * dir
        if (x > y) return 1 * dir
        return 0
      })
    },

    chartOptions() {
      return {
        labels: ['Laki-laki', 'Perempuan'],
        colors: ['#3b82f6', '#db2777'],
        legend: { position: 'bottom' },
      }
    },
  },

  watch: {
    form: {
      handler: throttle(function () {
        const q = pickBy(this.form)
        this.$inertia.get(
          this.route('report.universitas.detail', this.universitas),
          q,
          { preserveState: true, preserveScroll: true },
        )
      }, 200),
      deep: true,
    },
  },

  methods: {
    // TOGGLE SORT
    toggleSort(field) {
      if (this.sort.field === field) {
        this.sort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort.field = field
        this.sort.direction = 'asc'
      }
    },

    formatRupiah(v) {
      if (!v) return '-'
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(v)
    },
  },
}
</script>
